import { Check, Link2 } from "lucide-react";
import type { ClientSession } from "@/shared/hooks/useClientSessions";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { workspaceCopy } from "./copy";
import type { SessionStep } from "./sessionRoutes";
import { conversationFlowCopy, hasSessionTranscript } from "./sessionFlow";
import { conversationAnalysisCopy } from "./conversationAnalysisCopy";

export function SessionTrail({
  name,
  session,
  step,
  onStep,
  simple = false,
}: {
  simple?: boolean;
  name: string;
  session: ClientSession;
  step: SessionStep | "record";
  onStep: (step: SessionStep | "record") => void;
}) {
  const { locale } = useLanguage();
  const copy = workspaceCopy(locale);
  const flow = conversationFlowCopy(locale);
  const analysisCopy = conversationAnalysisCopy(locale);
  const date =
    session.recorded_at || session.scheduled_at || session.created_at;
  const hasTranscript = hasSessionTranscript(session);
  let steps: {
    id: SessionStep | "record";
    label: string;
    done: boolean;
    enabled: boolean;
  }[] = [
    {
      id: "recording" as const,
      label: simple ? analysisCopy.source : copy.recording,
      done: !!session.audio_path || hasTranscript,
      enabled: true,
    },
    {
      id: "analysis" as const,
      label: simple ? analysisCopy.analysis : flow.review,
      done: !!session.summary,
      enabled: hasTranscript,
    },
    {
      id: "note" as const,
      label: copy.note,
      done: !!session.notes?.trim(),
      enabled: true,
    },
  ];
  if (simple) steps = [steps[1]!, steps[0]!, steps[2]!];
  // The archived clinical record view still renders this shared trail with
  // `step="record"`. General Dashboard ends at Note and never exposes that
  // retired destination.
  if (step === "record")
    steps.push({
      id: "record",
      label: copy.record,
      done: false,
      enabled: true,
    });
  return (
    <div className="eq-session-trail">
      <div className="eq-session-trail-inner">
        <div className="eq-session-context">
          <div>
            <strong>{name}</strong>
            <small>
              {new Date(date).toLocaleString(locale, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {session.duration_seconds
                ? ` · ${Math.round(session.duration_seconds / 60)} min`
                : ""}
            </small>
          </div>
          <span className="eq-session-linked">
            <Link2 size={12} aria-hidden="true" />
            {copy.linked}
          </span>
        </div>
        <nav className="eq-session-steps" aria-label="Session workflow">
          {steps.map((item, index) => (
            <button
              key={item.id}
              disabled={!item.enabled}
              onClick={() => onStep(item.id)}
              aria-current={item.id === step ? "step" : undefined}
            >
              {!simple && (
                <span
                  className={`eq-step-number ${item.done ? "is-done" : ""}`}
                >
                  {item.done ? (
                    <Check size={11} aria-hidden="true" />
                  ) : (
                    index + 1
                  )}
                </span>
              )}
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
